import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  ArrowDownToLine,
  CheckCircle2,
  Tv,
  Clock,
  Sparkles,
  Loader2,
  Cpu,
  AlertCircle,
  TrendingUp,
  ShieldCheck,
  Bot,
} from 'lucide-react';
import { formatCoinAmount } from '@nova-mine/shared';
import { MiningReactor } from '../components/common/MiningReactor.js';
import { AnimatedCoinCounter } from '../components/common/AnimatedCoinCounter.js';
import { GlassCard } from '../components/common/GlassCard.js';
import { AnimatedButton } from '../components/common/AnimatedButton.js';
import { MinerStatusBadge } from '../components/common/MinerStatusBadge.js';
import { useAdsgram } from '../hooks/useAdsgram.js';
import { sound } from '../utils/sound.js';

interface MainMiningScreenProps {
  mainAccrued: number;
  lastClaimedAtMs: number;
  isClaiming: boolean;
  claimSuccessMessage: string | null;
  onClaim: () => Promise<any>;
  onTriggerFloatingCoins: () => void;
  onBoostCompleted?: () => void;
  accountBalance?: number;
  onSwitchToAuto?: () => void;
}

export const MainMiningScreen: React.FC<MainMiningScreenProps> = ({
  mainAccrued,
  lastClaimedAtMs,
  isClaiming,
  claimSuccessMessage,
  onClaim,
  onTriggerFloatingCoins,
  onBoostCompleted,
  accountBalance = 0,
  onSwitchToAuto,
}) => {
  const [elapsedString, setElapsedString] = useState('00h 00m');
  const [progressPercent, setProgressPercent] = useState(0);
  const [boostSuccess, setBoostSuccess] = useState<string | null>(null);

  const {
    playTwoAdSequence,
    adState,
    isPlaying,
    isSequenceRunning,
    currentSequenceStep,
    statusMessage,
    resetAdState,
  } = useAdsgram();

  // Daily target is 2,000 coins over 86,400 seconds
  const dailyTarget = 2000;
  const ratePerSecond = 2000 / 86400;
  const maxBufferSeconds = 86400;

  useEffect(() => {
    const updateProgress = () => {
      const now = Date.now();
      const elapsedMs = Math.max(0, now - lastClaimedAtMs);
      const elapsedSec = Math.min(maxBufferSeconds, elapsedMs / 1000);

      const percent = (elapsedSec / maxBufferSeconds) * 100;
      setProgressPercent(percent);

      const hours = Math.floor(elapsedSec / 3600);
      const mins = Math.floor((elapsedSec % 3600) / 60);
      setElapsedString(`${String(hours).padStart(2, '0')}h ${String(mins).padStart(2, '0')}m`);
    };

    updateProgress();
    const interval = setInterval(updateProgress, 1000);
    return () => clearInterval(interval);
  }, [lastClaimedAtMs]);

  const canClaim = mainAccrued >= 0.0001;

  const handleClaim = async () => {
    if (!canClaim || isClaiming) return;
    try {
      await onClaim();
    } catch {
      // handled in parent
    }
  };

  const handleWatchAdSequence = async () => {
    if (isPlaying || isSequenceRunning) return;
    sound.playTap();

    const res = await playTwoAdSequence(() => {
      sound.playCoinChime();
    });

    if (res.success) {
      sound.playClaimSuccess();
      onTriggerFloatingCoins();
      setBoostSuccess(`+${res.rewardCoins || 100} NOVA Sequence Boost Credited!`);
      setTimeout(() => setBoostSuccess(null), 4000);
      if (onBoostCompleted) onBoostCompleted();
    } else {
      sound.playTap();
    }
  };

  return (
    <div className="w-full space-y-5 animate-fade-in pb-4">
      {/* 1. Mining Fleet Sub-Navigation Switcher */}
      {onSwitchToAuto && (
        <div className="flex items-center p-1 rounded-2xl glass-panel-deep border border-white/10 max-w-xs mx-auto">
          <button
            className="flex-1 py-1.5 px-3 rounded-xl text-xs font-bold transition-all bg-gradient-to-r from-amber-500/30 to-purple-500/30 text-amber-300 border border-amber-500/40 shadow-sm flex items-center justify-center space-x-1.5"
          >
            <Cpu className="w-3.5 h-3.5 text-nova-gold" />
            <span>Main Core</span>
          </button>
          <button
            onClick={onSwitchToAuto}
            className="flex-1 py-1.5 px-3 rounded-xl text-xs font-bold transition-all text-gray-400 hover:text-white flex items-center justify-center space-x-1.5"
          >
            <Bot className="w-3.5 h-3.5" />
            <span>Autonomous</span>
          </button>
        </div>
      )}

      {/* 2. Main Mining Reactor Card */}
      <GlassCard variant="gold" className="border-amber-500/35 flex flex-col items-center text-center p-6 relative">
        {/* Floating Success Alert */}
        <AnimatePresence>
          {(claimSuccessMessage || boostSuccess) && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.9 }}
              className="absolute top-4 z-20 flex items-center space-x-2 bg-emerald-500/90 text-white font-black text-xs px-4 py-2 rounded-full shadow-xl backdrop-blur-md border border-emerald-400/40"
            >
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>{claimSuccessMessage || boostSuccess}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Top Badges */}
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            <span className="text-[11px] font-black uppercase tracking-widest text-amber-200">
              Gold Hash Core 01
            </span>
          </div>

          <MinerStatusBadge status={canClaim ? 'active' : 'active'} label="Continuous" />
        </div>

        {/* Central Mining Reactor */}
        <div className="my-6 flex items-center justify-center relative">
          <MiningReactor
            size={220}
            progressPercent={progressPercent}
            ratePerSecond={ratePerSecond}
            status={isClaiming ? 'claiming' : 'mining'}
            variant="gold"
          />
        </div>

        {/* Live Accrued Numeric Counter Display */}
        <div className="w-full max-w-sm bg-black/35 rounded-2xl p-4 border border-white/10 backdrop-blur-md">
          <span className="text-[11px] font-bold uppercase tracking-wider text-amber-200/80 block">
            Accrued Unclaimed Coins
          </span>

          <div className="mt-1 flex items-baseline justify-center space-x-2">
            <AnimatedCoinCounter
              value={mainAccrued}
              ratePerSecond={ratePerSecond}
              decimals={4}
              className="text-3xl sm:text-4xl font-black font-mono text-gold-gradient tracking-tight"
            />
            <span className="text-sm font-black text-amber-300 font-mono">NOVA</span>
          </div>

          <div className="mt-2.5 pt-2 border-t border-white/10 flex items-center justify-between text-xs text-gray-400">
            <span className="flex items-center space-x-1">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <span>Elapsed: <strong className="text-white font-mono">{elapsedString}</strong></span>
            </span>
            <span className="font-mono text-cyan-300 font-bold">
              +{(ratePerSecond).toFixed(4)}/s
            </span>
          </div>
        </div>

        {/* Claim Reward Button */}
        <div className="w-full max-w-sm mt-5">
          <AnimatedButton
            variant="gold"
            size="lg"
            onClick={handleClaim}
            disabled={!canClaim || isClaiming}
            isLoading={isClaiming}
            icon={<ArrowDownToLine className="w-5 h-5" />}
            className="w-full justify-center shadow-amber-500/25"
          >
            Claim Accrued Coins
          </AnimatedButton>
        </div>
      </GlassCard>

      {/* 3. AdsGram 2-Ad Sequence Boost Card */}
      <GlassCard variant="purple" className="border-purple-500/30 p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-2xl bg-purple-500/20 border border-purple-400/35 flex items-center justify-center text-purple-300 flex-shrink-0 shadow-lg shadow-purple-500/20">
              <Tv className="w-6 h-6 text-purple-300" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h4 className="text-sm font-black text-white">AdsGram Hash Boost</h4>
                <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
                  +100 NOVA
                </span>
              </div>
              <p className="text-xs text-gray-300 mt-1">
                Watch a 2-video sponsored sequence to inject immediate hashpower rewards.
              </p>
            </div>
          </div>
        </div>

        {/* Ad Sequence Status Indicators */}
        <div className="mt-4 pt-3 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center space-x-2 text-xs text-gray-400">
            <span className="flex items-center space-x-1">
              <span className={`w-2 h-2 rounded-full ${currentSequenceStep >= 1 ? 'bg-emerald-400' : 'bg-gray-600'}`} />
              <span>Step 1: Rewarded</span>
            </span>
            <span>•</span>
            <span className="flex items-center space-x-1">
              <span className={`w-2 h-2 rounded-full ${currentSequenceStep >= 2 ? 'bg-emerald-400' : 'bg-gray-600'}`} />
              <span>Step 2: Interstitial</span>
            </span>
          </div>

          <AnimatedButton
            variant="purple"
            size="md"
            onClick={handleWatchAdSequence}
            disabled={isPlaying || isSequenceRunning}
            isLoading={isPlaying || isSequenceRunning}
            icon={<Tv className="w-4 h-4" />}
            className="w-full sm:w-auto"
          >
            {isSequenceRunning ? `Step ${currentSequenceStep}/2 Running...` : 'Watch & Boost'}
          </AnimatedButton>
        </div>
      </GlassCard>
    </div>
  );
};
